import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  scheduleSearchParamsWrite,
  usePersistedSearchParam,
} from '@/hooks/usePersistedSearchParam';
import { getStorageItem, STORAGE_KEYS } from '@/libs/storage';

export const ZONE_PARAM = 'zone';
export const LOCATION_PARAM = 'location';
export const DEAL_PARAM = 'deal';

/** The cards that share the left slot, in the order one wins when several are present. */
const SLOT_PARAMS = [
  { param: LOCATION_PARAM, storageKey: STORAGE_KEYS.LAST_LOCATION_ID },
  { param: DEAL_PARAM, storageKey: STORAGE_KEYS.LAST_DEAL_ID },
  { param: ZONE_PARAM, storageKey: STORAGE_KEYS.LAST_ZONE_ID },
] as const;

export interface UseMapSelectionResult {
  /** The selected zone id from `?zone=<id>`, or `null` when absent. */
  zoneId: string | null;
  /** The selected location id from `?location=<id>`, or `null` when absent. */
  locationId: string | null;
  /** Selects a zone (history-replace). */
  selectZone: (id: string) => void;
  /** Clears the zone selection (history-replace). */
  clearZone: () => void;
  /** Selects a location (history-replace). */
  selectLocation: (id: string) => void;
  /** Clears the location selection (history-replace). */
  clearLocation: () => void;
  /** The selected deal id from `?deal=<id>`, or `null` when absent. */
  dealId: string | null;
  /** Selects a deal (history-replace). */
  selectDeal: (id: string) => void;
  /** Clears the deal selection (history-replace). */
  clearDeal: () => void;
}

/**
 * Owns zone, deal and location selection on the Map surface, as `/map` query
 * params and ephemeral view-state (history writes replace, like the active
 * workspace in `useActiveWorkspace`). Backed by `usePersistedSearchParam`: on
 * a cold mount with no param, the last-used card restores from `localStorage`,
 * so returning to `/map` reaches the setup left behind.
 */
export function useMapSelection(): UseMapSelectionResult {
  const [searchParams, setSearchParams] = useSearchParams();
  // The left slot holds one card, so one param wins on entry: the first slot the
  // URL names, else the first with a stored copy. A shared ?zone= link never gets
  // a restored deal layered beside it, and a hand-crafted URL naming two drops
  // the loser, once.
  const [entry] = useState(() => {
    const named = SLOT_PARAMS.filter(({ param }) => searchParams.has(param));
    const winner =
      named[0] ?? SLOT_PARAMS.find(({ storageKey }) => getStorageItem(storageKey) !== null);
    return { winner: winner?.param ?? null, dropped: named.slice(1).map(({ param }) => param) };
  });

  const zone = usePersistedSearchParam({
    param: ZONE_PARAM,
    storageKey: STORAGE_KEYS.LAST_ZONE_ID,
    restore: entry.winner === ZONE_PARAM,
  });
  const location = usePersistedSearchParam({
    param: LOCATION_PARAM,
    storageKey: STORAGE_KEYS.LAST_LOCATION_ID,
    restore: entry.winner === LOCATION_PARAM,
  });
  const deal = usePersistedSearchParam({
    param: DEAL_PARAM,
    storageKey: STORAGE_KEYS.LAST_DEAL_ID,
    restore: entry.winner === DEAL_PARAM,
  });

  // The drop rides the same batched write as the restores (the workspace's, say),
  // which would otherwise rebuild from a snapshot that still carries the losers.
  const slots = { [ZONE_PARAM]: zone, [LOCATION_PARAM]: location, [DEAL_PARAM]: deal };
  // biome-ignore lint/correctness/useExhaustiveDependencies: entry-URL check, mount only
  useEffect(() => {
    if (!entry.dropped.length) return;
    for (const param of entry.dropped) slots[param].dismiss();
    scheduleSearchParamsWrite(setSearchParams, (next) => {
      for (const param of entry.dropped) next.delete(param);
    });
  }, []);

  // Selecting a zone, a deal, or a location drops the other two in the SAME URL
  // write: a second sequential setSearchParams call can resurrect the param the
  // first removed. dismiss() drops the stored copy too, so a closed zone does
  // not restore itself beside the card that replaced it.
  const dismissZone = zone.dismiss;
  const dismissDeal = deal.dismiss;
  const dismissLocation = location.dismiss;
  const selectZone = useCallback(
    (id: string) => {
      dismissDeal();
      dismissLocation();
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(DEAL_PARAM);
          next.delete(LOCATION_PARAM);
          next.set(ZONE_PARAM, id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, dismissDeal, dismissLocation],
  );
  const selectLocation = useCallback(
    (id: string) => {
      dismissDeal();
      dismissZone();
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(DEAL_PARAM);
          next.delete(ZONE_PARAM);
          next.set(LOCATION_PARAM, id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, dismissDeal, dismissZone],
  );
  const selectDeal = useCallback(
    (id: string) => {
      dismissLocation();
      dismissZone();
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(LOCATION_PARAM);
          next.delete(ZONE_PARAM);
          next.set(DEAL_PARAM, id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, dismissLocation, dismissZone],
  );

  return {
    zoneId: zone.value,
    locationId: location.value,
    selectZone,
    clearZone: zone.clear,
    selectLocation,
    clearLocation: location.clear,
    dealId: deal.value,
    selectDeal,
    clearDeal: deal.clear,
  };
}
